import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperience,
  IExperienceTemplate,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import { DEMO_ORG_IDS } from '@dadohr/shared/domainTypes/org';
import styled from '@emotion/styled';
import { Divider } from '@material-ui/core';
import moment from 'moment-timezone';
import { useMemo } from 'react';
import { UnsafeTFunction, useUnsafeTranslation } from '../../../../../i18next';
import { collectExperienceStatsForActor } from '../../../../../services/experiences/instance';
import { addToTs, toMoment } from '../../../../../services/time';
import {
  AnchorField,
  getAbsoluteAnchorDefinition
} from '../../../components/Anchors';
import { ExperienceStatChips } from '../StatChips';

const Body = styled('div')((p) => ({
  padding: p.theme.spacing(2),
  minWidth: 320
}));

const Heading = styled('div')((p) => ({
  fontSize: p.theme.custom.fontSize.s,
  color: p.theme.custom.colors.gray.w500,
  textTransform: 'uppercase',
  marginBottom: p.theme.spacing()
}));

const Title = styled('div')((p) => ({
  fontSize: p.theme.custom.fontSize.m,
  fontWeight: p.theme.typography.fontWeightBold,
  marginBottom: p.theme.spacing(2)
}));

const Row = styled('div')((p) => ({
  color: p.theme.custom.colors.gray.w500,
  marginBottom: p.theme.spacing(),
  display: 'flex',
  alignItems: 'center',
  gap: p.theme.spacing()
}));

const getCohortDates = (
  tpl: Doc<IExperienceTemplate>,
  d: Doc<IExperience>,
  anchorFields: AnchorField[],
  t: UnsafeTFunction
) => {
  const customAnchorDefinition = getAbsoluteAnchorDefinition(
    d.data.startsAt,
    tpl.data.anchor,
    d.data.anchorDate,
    anchorFields,
    t
  );
  if (customAnchorDefinition) {
    // const anchorTime = addToTs(
    //   d.data.startsAt,
    //   customAnchorDefinition.position
    // );
    // const virtualStartDate = addToTs(
    //   anchorTime,
    //   customAnchorDefinition.originalOffset
    // ); // duration is a negative value
    return {
      startDate: toMoment(
        addToTs(d.data.anchorDate.at, -customAnchorDefinition.position)
      ),
      anchorData: {
        date: toMoment(d.data.anchorDate.at),
        label: customAnchorDefinition.label
      }
    };
  } else {
    const startDate = toMoment(d.data.anchorDate.at);
    return {
      startDate,
      anchorData: null
    };
  }
};

const DividerContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(2),
  marginBottom: p.theme.spacing(2),
  marginLeft: -p.theme.spacing(2), // negative margin to go full width with the divider
  marginRight: -p.theme.spacing(2)
}));

export const TaskTimelineAnnotationPopoverBody = ({
  tpl,
  instances,
  anchorFields
}: {
  instances: Doc<IExperience>[];
  tpl: Doc<IExperienceTemplate>;
  anchorFields: AnchorField[];
}) => {
  const { t } = useUnsafeTranslation();
  const stats = useMemo(
    () => collectExperienceStatsForActor(instances, PARTICIPANT_ACTOR_KEY),
    [instances]
  );
  if (!instances.length) {
    // will never happen
    return null;
  }
  const firstInstance = instances[0];
  const cohortDates = getCohortDates(tpl, firstInstance, anchorFields, t);
  const daysIn = moment().diff(cohortDates.startDate, 'd');
  return (
    <Body>
      <Heading>{t('Participants')}</Heading>
      <Title>
        {cohortDates.anchorData
          ? t('Day {{ day }}, {{ label }} {{ date }}', {
              day: daysIn + 1,
              date: cohortDates.anchorData.date
                .clone()
                .tz(tpl.data.tz)
                .format('L'),
              label: cohortDates.anchorData.label
            })
          : t('Day {{ day }}, started on {{ date }}', {
              day: daysIn + 1,
              date: cohortDates.startDate.clone().tz(tpl.data.tz).format('L')
            })}{' '}
      </Title>
      <Row>{t('participantsWithCount', { count: instances.length })}</Row>

      {stats && (
        <>
          <DividerContainer>
            <Divider light />
          </DividerContainer>
          <ExperienceStatChips
            stats={stats}
            v={DEMO_ORG_IDS.includes(tpl.data.orgId) ? 2 : 1}
          />
        </>
      )}
    </Body>
  );
};
